import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScanLine, File, X } from 'lucide-react';
import { LANGUAGES, LANGUAGE_VARIANTS, TARGET_LANGUAGE_CODES } from '../constants/languages';
import { MAX_DOCUMENT_SIZE_BYTES, MAX_DOCUMENT_SIZE_MB } from '../constants/upload';
import { TranslationBriefFields, useTranslationBrief } from './TranslationBriefFields';
import { useAuth } from '../context/AuthContext';
import ScanMinimumInfo from './ScanMinimumInfo';
import './DocumentUpload.css';

const SUPPORTED_SCAN_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.tif', '.tiff', '.webp', '.docx'];

function hasSupportedExtension(name) {
  const lower = name.toLowerCase();
  return SUPPORTED_SCAN_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function validateScanFile(candidate, t) {
  if (!hasSupportedExtension(candidate.name)) {
    alert(t('upload.fileTypeError'));
    return false;
  }
  if (candidate.size > MAX_DOCUMENT_SIZE_BYTES) {
    alert(t('upload.fileTooLarge', { maxMb: MAX_DOCUMENT_SIZE_MB }));
    return false;
  }
  return true;
}

function ScanUpload({ onUpload, onUploadStart, disabled }) {
  const { t, i18n } = useTranslation('scans');
  const { user } = useAuth();
  // Only metered users are billed, so only they get the per-page minimum served with their balance.
  const scanPageMinimum = user?.char_usage?.scan_min_chars_per_page;
  const [file, setFile] = useState(null);
  const [translate, setTranslate] = useState(true);
  const [languageTo, setLanguageTo] = useState('en');
  const [languageVariant, setLanguageVariant] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const { brief, setBrief, context } = useTranslationBrief();

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateScanFile(droppedFile, t)) {
        setFile(droppedFile);
      }
    }
  }, [t]);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateScanFile(selectedFile, t)) {
        setFile(selectedFile);
      }
    }
  };

  const handleUpload = () => {
    if (!file) return;
    if (onUploadStart) onUploadStart();
    onUpload(file, { translate, languageTo, languageVariant: languageVariant || '', context });
  };

  const removeFile = () => setFile(null);

  const handleLanguageChange = (code) => {
    setLanguageTo(code);
    const variants = LANGUAGE_VARIANTS[code];
    setLanguageVariant(variants ? variants[0].code : null);
  };

  const variants = LANGUAGE_VARIANTS[languageTo];

  const languages = TARGET_LANGUAGE_CODES
    .map((code) => ({
      code,
      name: t(`chat:languages.${code}`, { defaultValue: code }),
      flag: LANGUAGES[code]?.flag || '🌐',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="document-upload">
      <div
        className={`drop-zone ${dragActive ? 'active' : ''} ${file ? 'has-file' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {!file ? (
          <>
            <ScanLine size={48} className="upload-icon" />
            <p className="drop-text">
              {t('upload.dragDrop')}{' '}
              <label className="browse-link">
                {t('upload.browse')}
                <input
                  type="file"
                  accept={SUPPORTED_SCAN_EXTENSIONS.join(',')}
                  onChange={handleFileSelect}
                  hidden
                />
              </label>
            </p>
            <p className="drop-hint">{t('upload.hint', { maxMb: MAX_DOCUMENT_SIZE_MB })}</p>
          </>
        ) : (
          <div className="file-preview">
            <File size={32} className="file-icon" />
            <div className="file-info">
              <span className="file-name">{file.name}</span>
              <span className="file-size">
                {file.size >= 1024 * 1024
                  ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                  : `${(file.size / 1024).toFixed(1)} KB`}
              </span>
            </div>
            <button className="btn btn-ghost remove-btn" onClick={removeFile}>
              <X size={18} />
            </button>
          </div>
        )}
      </div>
      {scanPageMinimum != null && (
        <p className="drop-hint scan-minimum-note">
          {t('common:scanPageMinimum', { chars: scanPageMinimum.toLocaleString(i18n.language) })}
          <ScanMinimumInfo />
        </p>
      )}

      {file && (
        <div className="upload-options">
          <label className="scan-translate-toggle">
            <input
              type="checkbox"
              checked={translate}
              onChange={(e) => setTranslate(e.target.checked)}
              data-testid="scan-translate-toggle"
            />
            <span>{t('options.translate')}</span>
            {!translate && <span className="scan-translate-hint">{t('options.recognitionHint')}</span>}
          </label>

          {translate && (
          <>
          <div className="language-select">
            <label>{t('languages.to')}</label>
            <select
              value={languageTo}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="input"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>

          {variants && (
            <div className="language-select">
              <label>{t('languages.variantLabel')}</label>
              <select
                value={languageVariant || ''}
                onChange={(e) => setLanguageVariant(e.target.value)}
                className="input"
              >
                {variants.map((v) => (
                  <option key={v.code} value={v.code}>
                    {LANGUAGES[v.code]?.flag || '🌐'} {t(`chat:languages.${v.nameKey}`, { defaultValue: v.nameKey })}
                  </option>
                ))}
              </select>
            </div>
          )}

          <TranslationBriefFields brief={brief} setBrief={setBrief} />
          </>
          )}

          <button
            className="btn btn-primary upload-btn"
            onClick={handleUpload}
            disabled={disabled}
          >
            {disabled ? (
              <>
                <div className="spinner" />
                {t('buttons.translating')}
              </>
            ) : (
              t(translate ? 'buttons.translate' : 'buttons.recognise')
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default ScanUpload;
